import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import set from 'lodash/set';

import {
  arrayToObject,
  deleteProperty,
  omitProperties,
} from 'src/presentation/components/pages/sidekick/threads/utils/utils';
import { ExternalAction, ExternalActionInputOrResult, TypeSpec } from 'src/models/Sidekick/SidekickModuleSpec';
import generateId from 'src/lib/generateId';
import { NodesTypes, flattenSteps } from './utils';
import SidekickThread, { NextData } from 'src/models/Sidekick/SidekickThreads';
import { ENTER_MANUALLY_OPTION } from '../components/SidePanel/Components/Rule/useCondition';

enum ExternalActionFields {
  INPUTS = 'inputs',
  RESULTS = 'results',
}

type DiffContext = {
  addedKeys: string[];
  changedKeys: string[];
  deletedKeys: string[];
  addKeys: (addedKeys: string[], changedKeys: string[], deletedKeys: string[]) => void;
};

export type UpdateModuleCallbackParams = {
  attr: string;
  moduleData: NextData;
};
type UpdateCallbackFn = (params: UpdateModuleCallbackParams) => void;

export const checkWithExternalAction = (external: ExternalAction, current: ExternalAction) => {
  const ctx: DiffContext = {
    addedKeys: [],
    changedKeys: [],
    deletedKeys: [],
    addKeys: (addedKeys, changedKeys, deletedKeys) => {
      ctx.addedKeys.push(...addedKeys);
      ctx.changedKeys.push(...changedKeys);
      ctx.deletedKeys.push(...deletedKeys);
    },
  };

  const [inputs, isInputsDifferent] = checkExternalActionInputsOrResults(
    external.inputs,
    current.inputs,
    ExternalActionFields.INPUTS,
    ctx
  );

  const [results, isResultsDifferent] = checkExternalActionInputsOrResults(
    external.results,
    current.results,
    ExternalActionFields.RESULTS,
    ctx
  );

  const isDifferent = isInputsDifferent || isResultsDifferent;
  return { ctx, inputs, results, isDifferent };
};

export const externalActionsDiff = async (
  currentThread: SidekickThread,
  externalActions: ExternalAction[],
  updateCallback?: UpdateCallbackFn
) => {
  const errors: { attr: string; code: string; detail: string }[] = [];

  const modsWithExternalActions = flattenSteps(currentThread.nextData)
    .filter(mod => mod.nodeType === NodesTypes.MODULE)
    .filter(mod => (mod.mod as NextData).externalAction);

  for (const modOrModifier of modsWithExternalActions) {
    const mod = modOrModifier.mod as NextData;
    const currentExternalAction = mod.externalAction as ExternalAction;

    const externalActionSpec = externalActions.find(
      extAction =>
        extAction.integrationId === currentExternalAction.integrationId && extAction.name === currentExternalAction.name
    );
    const integrationName =
      currentExternalAction.displayName || currentExternalAction.name || currentExternalAction.integrationId;

    if (!externalActionSpec) {
      const errorMessage = `The latest "${integrationName}" app version is not compatible with the existing thread`;

      const errorExists = errors.find(error => error.detail === errorMessage);
      if (!errorExists) {
        errors.push({ attr: modOrModifier.attr!, code: 'notExists', detail: errorMessage });
      }
      continue;
    }

    const { inputs, results, isDifferent, ctx } = checkWithExternalAction(externalActionSpec, currentExternalAction);
    if (isDifferent) {
      if (updateCallback) {
        const clonedMod = cloneDeep(mod);
        clonedMod.id = generateId(); // to force re-render
        clonedMod.externalAction = { ...externalActionSpec!, inputs, results };

        updateCallback({ attr: modOrModifier.attr!, moduleData: clonedMod });
      }

      let warningStr = '';
      if (ctx.addedKeys.length) {
        warningStr += `Added fields: ${ctx.addedKeys.join(', ')}. `;
      }

      if (ctx.deletedKeys.length) {
        warningStr += `Deleted fields: ${ctx.deletedKeys.join(', ')}. `;
      }

      if (ctx.changedKeys.length) {
        warningStr += `Changed fields: ${ctx.changedKeys.join(', ')}. `;
      }

      if (warningStr) {
        errors.push({
          attr: modOrModifier.attr!,
          code: 'warning',
          detail: `The latest "${integrationName}" app version has changed. ${warningStr}`,
        });
      }
    }
  }
  return errors;
};

const checkExternalActionInputsOrResults = (
  extActionInputsOrResults: ExternalActionInputOrResult[],
  currentExtActionInputsOrResults: ExternalActionInputOrResult[],
  externalActionField: ExternalActionFields,
  ctx: DiffContext
): [ExternalActionInputOrResult[], boolean] => {
  const inputs = cloneDeep(currentExtActionInputsOrResults) as ExternalActionInputOrResult[];
  const currentExtActionsInputsMap = arrayToObject<ExternalActionInputOrResult>(
    inputs as ExternalActionInputOrResult[],
    'name'
  );
  const extActionsInputsMap = arrayToObject<ExternalActionInputOrResult>(extActionInputsOrResults, 'name');

  const deletedInputs = Object.keys(currentExtActionsInputsMap).filter(key => !extActionsInputsMap[key]);

  const addedInputs = Object.keys(extActionsInputsMap).filter(key => !currentExtActionsInputsMap[key]);

  const changedInputs = Object.keys(currentExtActionsInputsMap).filter(
    key =>
      deletedInputs.indexOf(key) === -1 &&
      !isEqual(
        {
          ...currentExtActionsInputsMap[key],
          value: null,
        },
        { ...extActionsInputsMap[key], value: null }
      )
  );

  const isStructureDifferent = deletedInputs.length || addedInputs.length || changedInputs.length;

  if (isStructureDifferent) {
    // added inputs
    if (addedInputs && addedInputs.length) {
      addedInputs.forEach(inputName => {
        const input = extActionsInputsMap[inputName];
        inputs.push(input!);
        ctx.addedKeys.push(`${externalActionField}.${inputName}`);
      });
    }
    // deleted
    if (deletedInputs && deletedInputs.length) {
      deletedInputs.forEach(inputName => {
        const index = inputs.findIndex(input => input.name === inputName);
        inputs.splice(index, 1);
        ctx.deletedKeys.push(`${externalActionField}.${inputName}`);
      });
    }
    // changed
    changedInputs.forEach(inputName => {
      const input = extActionsInputsMap[inputName];
      const currentInput = currentExtActionsInputsMap[inputName];

      let value = transformValue(currentInput!.value, currentInput!.typeSpec.type);

      // if inputs are equal do nothing
      if (!isEqual(input, omitProperties(currentInput, ['value']))) {
        differenceTypeSpecs(input!.typeSpec, currentInput!.typeSpec, `${externalActionField}.${inputName}`, ctx);

        // replace input but not value
        const index = inputs.findIndex(input => input.name === inputName);

        if (externalActionField === ExternalActionFields.INPUTS) {
          if (!isEnterManuallyOption(value)) {
            ctx.addedKeys.forEach(key => {
              if (value) {
                const path = removeFirstTwoProps(key).replace('props.', '');
                if (path) {
                  set(value, path, null);
                }
              }
            });

            ctx.deletedKeys.forEach(key => {
              if (value) {
                const path = removeFirstTwoProps(key).replace('props.', '');
                if (path) {
                  deleteProperty(value, path);
                }
              }
            });

            value = JSON.stringify(value, null, 2);
          }

          inputs[index] = {
            ...(input as ExternalActionInputOrResult),
            value,
          };
        } else {
          inputs[index] = {
            ...(input as ExternalActionInputOrResult),
          };
        }
      }
    });
  }
  return [inputs, !!isStructureDifferent];
};

function differenceTypeSpecs(
  integrationTypeSpec: TypeSpec,
  currentTypeSpec: TypeSpec,
  propertyName: string,
  ctx: DiffContext
): void {
  if (integrationTypeSpec.type !== currentTypeSpec.type) {
    ctx.changedKeys.push(propertyName);
  }

  if (integrationTypeSpec.type === 'object') {
    differenceTypeSpecsProps(integrationTypeSpec.props || {}, currentTypeSpec.props || {}, `${propertyName}`, ctx);

    differenceTypeSpecsProps(
      integrationTypeSpec.items?.props || {},
      currentTypeSpec.items?.props || {},
      `${propertyName}.items`,
      ctx
    );
  }
}

function differenceTypeSpecsProps(
  external: Record<string, TypeSpec>,
  current: Record<string, TypeSpec>,
  parentPropName = '',
  ctx: DiffContext
) {
  const deletedProps = Object.keys(current).filter(key => !external[key]);
  ctx.deletedKeys.push(...deletedProps.map((prop: string) => `${parentPropName}.${prop}`));

  const addedProps = Object.keys(external).filter(key => !current[key]);

  addedProps.forEach(prop => {
    if (external[prop]!.type !== 'object') {
      ctx.addedKeys.push(`${parentPropName}.${prop}`);
    } else {
      differenceTypeSpecs(
        external[prop]!,
        { type: external[prop]!.type } as TypeSpec,
        `${parentPropName}.${prop}`,
        ctx
      );
    }
  });

  const changedProps: string[] = Object.keys(current).filter(
    key => deletedProps.indexOf(key) === -1 && !isEqual(external[key], current[key])
  );

  for (const prop of changedProps) {
    if (current[prop] && (current[prop] as TypeSpec).type !== 'object') {
      ctx.changedKeys.push(`${parentPropName}.${prop}`);
      continue;
    }
    const integrationProp = external[prop];
    const currentProp = current[prop];

    if (!integrationProp || !currentProp) {
      throw new Error("no field 'prop' in integration or current typeSpecs");
    }

    differenceTypeSpecs(integrationProp, currentProp, `${parentPropName}.${prop}`, ctx);
  }
}

const isEnterManuallyOption = (value: unknown) =>
  value &&
  typeof value === 'object' &&
  (value as string[]).length >= 1 &&
  (value as string[])[0] === ENTER_MANUALLY_OPTION;

const transformValue = (value: unknown, type: string) => {
  if (isEnterManuallyOption(value)) {
    return value;
  }
  if (type === 'object' && typeof value === 'string') {
    return JSON.parse(value);
  }

  return value;
};

const removeFirstTwoProps = (inputString: string) => {
  const words = inputString.split('.');

  if (words.length >= 2) {
    words.splice(0, 2);
    return words.join('.');
  } else {
    return inputString;
  }
};
